import React, { useState, useContext, createContext } from 'react';

// Addison Mail - Complete i18n Translations
export const translations = {
  en: {
    nav: {
      dashboard: 'Dashboard',
      inbox: 'Inbox',
      compose: 'Compose',
      tempmail: 'TempMail',
      pgp: 'PGP Keys',
      settings: 'Settings',
      upgrade: 'Upgrade',
      login: 'Login',
      register: 'Get Started',
      logout: 'Logout',
      about: 'About',
      contact: 'Contact',
      admin: 'Admin'
    },
    landing: {
      heroTitle: 'Secure Email for',
      heroTitleGradient: 'Modern Privacy',
      heroSubtitle: 'Addison Mail provides anonymous, encrypted email with PGP support, temporary addresses, and crypto payments. Your privacy, secured.',
      ctaPrimary: 'Get Started Free',
      ctaSecondary: 'Try Temp Mail',
      newService: 'New Service - Be Among the First Users!',
      features: {
        title: 'Everything You Need',
        subtitle: 'Professional email features with privacy at the core',
        pgp: { title: 'PGP Encryption', desc: 'End-to-end encryption with OpenPGP. Generate keys, sign and encrypt emails securely.' },
        tempmail: { title: 'Temp Mail', desc: 'Create disposable email addresses instantly. No registration required for basic use.' },
        crypto: { title: 'Crypto Payments', desc: 'Pay with Bitcoin, Ethereum, or Solana. No credit cards, no personal info needed.' },
        tor: { title: 'Tor Compatible', desc: 'Full Tor hidden service support. Access your email anonymously from anywhere.' },
        modern: { title: 'Modern Interface', desc: 'Clean, fast web interface. Works perfectly on desktop and mobile devices.' },
        privacy: { title: 'Zero Knowledge', desc: 'We cannot read your emails. Your private keys never leave your device.' }
      },
      pricing: {
        title: 'Simple Pricing',
        subtitle: 'Pay with crypto, stay anonymous',
        free: { name: 'Free', price: '$0', period: 'Forever', cta: 'Get Started', features: ['1 Email Account', '100 MB Storage', '10 Sends/Day', '3 Temp Aliases'] },
        pro: { name: 'Pro', price: '$5', period: '/month', cta: 'Upgrade to Pro', features: ['5 Email Accounts', '1 GB Storage', 'Unlimited Sends', '20 Temp Aliases', 'PGP Encryption'] },
        enterprise: { name: 'Enterprise', price: '$20', period: '/month', cta: 'Contact Sales', features: ['20 Email Accounts', '10 GB Storage', 'Unlimited Sends', '100 Temp Aliases', 'Custom Domain'] }
      },
      cta: {
        title: 'Ready to Secure Your Communications?',
        subtitle: 'Join Addison Mail for private, encrypted email communication.',
        button: 'Create Free Account'
      }
    },
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to access your secure inbox',
      registerTitle: 'Create Account',
      registerSubtitle: 'Get your secure email address in seconds',
      username: 'Username',
      email: 'Email (optional)',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      signIn: 'Sign In',
      createAccount: 'Create Free Account',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      emailWillBe: 'Your email will be:',
      forgotPassword: 'Forgot password?',
      rememberMe: 'Remember me',
      secureConnection: 'Your connection is encrypted and anonymous',
      freeForever: 'Free forever plan available',
      noCreditCard: 'No credit card required',
      instantActivation: 'Instant activation'
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      stats: {
        emails: 'Emails',
        storage: 'Storage Used',
        sent: 'Sent Today',
        aliases: 'Active Aliases'
      },
      quickActions: {
        title: 'Quick Actions',
        compose: 'New Email',
        tempmail: 'Temp Mail',
        pgp: 'PGP Keys',
        settings: 'Settings'
      },
      recentEmails: 'Recent Emails',
      noEmails: 'No emails yet',
      viewAll: 'View All'
    },
    inbox: {
      title: 'Inbox',
      compose: 'Compose',
      refresh: 'Refresh',
      search: 'Search emails...',
      noEmails: 'No emails found',
      from: 'From',
      subject: 'Subject',
      date: 'Date',
      delete: 'Delete',
      reply: 'Reply',
      forward: 'Forward',
      download: 'Download',
      markRead: 'Mark as read',
      markUnread: 'Mark as unread',
      selectAll: 'Select all',
      deleteSelected: 'Delete selected'
    },
    compose: {
      title: 'Compose Email',
      from: 'From',
      to: 'To',
      cc: 'CC',
      bcc: 'BCC',
      subject: 'Subject',
      message: 'Message',
      attach: 'Attach Files',
      encrypt: 'Encrypt with PGP',
      sign: 'Sign with PGP',
      send: 'Send Email',
      saveDraft: 'Save Draft',
      discard: 'Discard',
      loading: 'Sending...',
      success: 'Email sent successfully',
      error: 'Failed to send email'
    },
    tempmail: {
      title: 'Temporary Email',
      create: 'Create Temp Address',
      refresh: 'Refresh Inbox',
      copy: 'Copy Address',
      delete: 'Delete Address',
      expiresIn: 'Expires in',
      noMessages: 'No messages yet',
      yourAddress: 'Your temporary address'
    },
    pgp: {
      title: 'PGP Key Management',
      generate: 'Generate New Key',
      import: 'Import Key',
      export: 'Export Key',
      delete: 'Delete Key',
      publicKey: 'Public Key',
      privateKey: 'Private Key',
      fingerprint: 'Fingerprint',
      created: 'Created',
      expires: 'Expires',
      noKeys: 'No PGP keys yet'
    },
    settings: {
      title: 'Settings',
      account: 'Account',
      security: 'Security',
      notifications: 'Notifications',
      appearance: 'Appearance',
      language: 'Language',
      username: 'Username',
      email: 'Email Address',
      password: 'Password',
      changePassword: 'Change Password',
      twoFactor: 'Two-Factor Authentication',
      enable2FA: 'Enable 2FA',
      darkMode: 'Dark Mode',
      save: 'Save Changes',
      saved: 'Settings saved successfully'
    },
    admin: {
      title: 'Admin Panel',
      dashboard: 'Dashboard',
      users: 'Users',
      broadcast: 'Broadcast',
      settings: 'Settings',
      audit: 'Audit Log',
      stats: {
        totalUsers: 'Total Users',
        newThisWeek: 'New This Week',
        totalEmails: 'Total Emails',
        proUsers: 'Pro Users'
      },
      searchUsers: 'Search users...',
      userActions: {
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        promote: 'Make Admin',
        demote: 'Remove Admin'
      }
    },
    footer: {
      description: 'Secure, anonymous email with PGP encryption. Built for privacy-conscious users.',
      product: 'Product',
      resources: 'Resources',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      dmca: 'DMCA',
      copyright: 'All rights reserved'
    },
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      save: 'Save',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      none: 'None',
      yes: 'Yes',
      no: 'No'
    }
  },
  fr: {
    nav: {
      dashboard: 'Tableau de bord',
      inbox: 'Boîte de réception',
      compose: 'Nouveau',
      tempmail: 'TempMail',
      pgp: 'Clés PGP',
      settings: 'Paramètres',
      upgrade: 'Premium',
      login: 'Connexion',
      register: 'Commencer',
      logout: 'Déconnexion',
      about: 'À propos',
      contact: 'Contact',
      admin: 'Admin'
    },
    landing: {
      heroTitle: 'Email Sécurisé pour',
      heroTitleGradient: 'La Vie Privée Moderne',
      heroSubtitle: 'Addison Mail fournit un email anonyme et chiffré avec support PGP, adresses temporaires et paiements crypto. Votre vie privée, sécurisée.',
      ctaPrimary: 'Commencer Gratuitement',
      ctaSecondary: 'Essayer Temp Mail',
      newService: 'Nouveau Service - Soyez Parmi les Premiers Utilisateurs!',
      features: {
        title: 'Tout Ce Dont Vous Avez Besoin',
        subtitle: 'Fonctionnalités email professionnelles axées sur la confidentialité',
        pgp: { title: 'Chiffrement PGP', desc: 'Chiffrement de bout en bout avec OpenPGP. Générez des clés, signez et chiffrez vos emails en toute sécurité.' },
        tempmail: { title: 'Email Temporaire', desc: 'Créez des adresses email jetables instantanément. Aucune inscription requise pour une utilisation de base.' },
        crypto: { title: 'Paiements Crypto', desc: "Payez avec Bitcoin, Ethereum ou Solana. Pas de cartes de crédit, pas d'informations personnelles." },
        tor: { title: 'Compatible Tor', desc: "Support complet du service caché Tor. Accédez à vos emails anonymement de n'importe où." },
        modern: { title: 'Interface Moderne', desc: 'Interface web propre et rapide. Fonctionne parfaitement sur ordinateur et mobile.' },
        privacy: { title: 'Zero Knowledge', desc: 'Nous ne pouvons pas lire vos emails. Vos clés privées ne quittent jamais votre appareil.' }
      },
      pricing: {
        title: 'Tarification Simple',
        subtitle: 'Payez en crypto, restez anonyme',
        free: { name: 'Gratuit', price: '0€', period: 'Pour toujours', cta: 'Commencer', features: ['1 Compte Email', '100 Mo Stockage', '10 Envoi/Jour', '3 Alias Temporaires'] },
        pro: { name: 'Pro', price: '5€', period: '/mois', cta: 'Passer à Pro', features: ['5 Comptes Email', '1 Go Stockage', 'Envoi Illimité', '20 Alias Temporaires', 'Chiffrement PGP'] },
        enterprise: { name: 'Entreprise', price: '20€', period: '/mois', cta: 'Contactez-nous', features: ['20 Comptes Email', '10 Go Stockage', 'Envoi Illimité', '100 Alias Temporaires', 'Domaine Personnalisé'] }
      },
      cta: {
        title: 'Prêt à Sécuriser Vos Communications?',
        subtitle: 'Rejoignez Addison Mail pour une communication email privée et chiffrée.',
        button: 'Créer un Compte Gratuit'
      }
    },
    auth: {
      loginTitle: 'Bon Retour',
      loginSubtitle: 'Connectez-vous pour accéder à votre boîte sécurisée',
      registerTitle: 'Créer un Compte',
      registerSubtitle: 'Obtenez votre adresse email sécurisée en quelques secondes',
      username: "Nom d'utilisateur",
      email: 'Email (optionnel)',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      signIn: 'Se Connecter',
      createAccount: 'Créer un Compte Gratuit',
      noAccount: 'Pas encore de compte?',
      hasAccount: 'Déjà un compte?',
      emailWillBe: 'Votre email sera:',
      forgotPassword: 'Mot de passe oublié?',
      rememberMe: 'Se souvenir de moi',
      secureConnection: 'Votre connexion est chiffrée et anonyme',
      freeForever: 'Plan gratuit disponible',
      noCreditCard: 'Pas de carte de crédit requise',
      instantActivation: 'Activation instantanée'
    },
    dashboard: {
      title: 'Tableau de bord',
      welcome: 'Bon retour',
      stats: {
        emails: 'Emails',
        storage: 'Stockage Utilisé',
        sent: 'Envoyés Aujourd\'hui',
        aliases: 'Alias Actifs'
      },
      quickActions: {
        title: 'Actions Rapides',
        compose: 'Nouvel Email',
        tempmail: 'Email Temp',
        pgp: 'Clés PGP',
        settings: 'Paramètres'
      },
      recentEmails: 'Emails Récents',
      noEmails: 'Aucun email',
      viewAll: 'Voir Tout'
    },
    inbox: {
      title: 'Boîte de Réception',
      compose: 'Nouveau',
      refresh: 'Actualiser',
      search: 'Rechercher des emails...',
      noEmails: 'Aucun email trouvé',
      from: 'De',
      subject: 'Sujet',
      date: 'Date',
      delete: 'Supprimer',
      reply: 'Répondre',
      forward: 'Transférer',
      download: 'Télécharger',
      markRead: 'Marquer comme lu',
      markUnread: 'Marquer comme non lu',
      selectAll: 'Tout sélectionner',
      deleteSelected: 'Supprimer la sélection'
    },
    compose: {
      title: 'Nouvel Email',
      from: 'De',
      to: 'À',
      cc: 'CC',
      bcc: 'CCI',
      subject: 'Sujet',
      message: 'Message',
      attach: 'Joindre des Fichiers',
      encrypt: 'Chiffrer avec PGP',
      sign: 'Signer avec PGP',
      send: 'Envoyer',
      saveDraft: 'Enregistrer Brouillon',
      discard: 'Annuler',
      loading: 'Envoi...',
      success: 'Email envoyé avec succès',
      error: 'Échec de l\'envoi'
    },
    tempmail: {
      title: 'Email Temporaire',
      create: 'Créer une Adresse',
      refresh: 'Actualiser',
      copy: 'Copier l\'Adresse',
      delete: 'Supprimer l\'Adresse',
      expiresIn: 'Expire dans',
      noMessages: 'Aucun message',
      yourAddress: 'Votre adresse temporaire'
    },
    pgp: {
      title: 'Gestion des Clés PGP',
      generate: 'Générer une Clé',
      import: 'Importer une Clé',
      export: 'Exporter une Clé',
      delete: 'Supprimer une Clé',
      publicKey: 'Clé Publique',
      privateKey: 'Clé Privée',
      fingerprint: 'Empreinte',
      created: 'Créée',
      expires: 'Expire',
      noKeys: 'Aucune clé PGP'
    },
    settings: {
      title: 'Paramètres',
      account: 'Compte',
      security: 'Sécurité',
      notifications: 'Notifications',
      appearance: 'Apparence',
      language: 'Langue',
      username: "Nom d'utilisateur",
      email: 'Adresse Email',
      password: 'Mot de passe',
      changePassword: 'Changer le Mot de Passe',
      twoFactor: 'Authentification à Deux Facteurs',
      enable2FA: 'Activer 2FA',
      darkMode: 'Mode Sombre',
      save: 'Enregistrer',
      saved: 'Paramètres enregistrés'
    },
    admin: {
      title: 'Panneau Admin',
      dashboard: 'Tableau de Bord',
      users: 'Utilisateurs',
      broadcast: 'Diffusion',
      settings: 'Paramètres',
      audit: 'Journal d\'Audit',
      stats: {
        totalUsers: 'Utilisateurs Totaux',
        newThisWeek: 'Nouveaux Cette Semaine',
        totalEmails: 'Emails Totaux',
        proUsers: 'Utilisateurs Pro'
      },
      searchUsers: 'Rechercher des utilisateurs...',
      userActions: {
        view: 'Voir',
        edit: 'Modifier',
        delete: 'Supprimer',
        promote: 'Rendre Admin',
        demote: 'Retirer Admin'
      }
    },
    footer: {
      description: 'Email sécurisé et anonyme avec chiffrement PGP. Conçu pour les utilisateurs soucieux de leur vie privée.',
      product: 'Produit',
      resources: 'Ressources',
      legal: 'Légal',
      privacy: 'Politique de Confidentialité',
      terms: "Conditions d'Utilisation",
      dmca: 'DMCA',
      copyright: 'Tous droits réservés'
    },
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      delete: 'Supprimer',
      edit: 'Modifier',
      save: 'Enregistrer',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      search: 'Rechercher',
      filter: 'Filtrer',
      all: 'Tout',
      none: 'Aucun',
      yes: 'Oui',
      no: 'Non'
    }
  }
};

export const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('fr'); // Default to French
  
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };
  
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}

